import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };
const cls = 'w-full px-3 py-2.5 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none font-body text-sm';

const EMPTY = { nama_kk: '', phone: '', alamat: '', jumlah_jiwa: 1 };

export default function ZakatMuzakkiPane({ mosqueId }) {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const LIMIT = 20;

  const load = async (pg = 1, search = q) => {
    try {
      const { data: res } = await axios.get(`${API_URL}/api/zakat-ramadan/muzakki`, {
        ...cfg, params: { q: search || undefined, page: pg, limit: LIMIT }
      });
      setData(res.data);
      setTotal(res.total);
      setPage(pg);
    } catch { }
  };

  useEffect(() => { load(1, ''); }, [mosqueId]);

  const handleSearch = (e) => {
    setQ(e.target.value);
    load(1, e.target.value);
  };

  const openAdd = () => { setForm(EMPTY); setEditing(null); setShowForm(true); };
  const openEdit = (row) => { setForm({ ...row }); setEditing(row.id); setShowForm(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.nama_kk.trim()) return toast.error('Nama KK wajib diisi');
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/zakat-ramadan/muzakki/${editing}`, form, cfg);
        toast.success('Muzakki diperbarui');
      } else {
        await axios.post(`${API_URL}/api/zakat-ramadan/muzakki`, form, cfg);
        toast.success('Muzakki ditambahkan');
      }
      setShowForm(false);
      load(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus data muzakki ini?')) return;
    try {
      await axios.delete(`${API_URL}/api/zakat-ramadan/muzakki/${id}`, cfg);
      toast.success('Muzakki dihapus');
      load(page);
    } catch {
      toast.error('Gagal menghapus');
    }
  };

  return (
    <div className="space-y-4" data-testid="zakat-muzakki">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-stone-400" />
          <input placeholder="Cari nama atau HP..." value={q} onChange={handleSearch}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none" />
        </div>
        <button onClick={openAdd} data-testid="add-muzakki-btn"
          className="flex items-center gap-2 px-4 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-sm font-medium transition-colors">
          <Plus className="w-4 h-4" /> Tambah Muzakki
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={handleSave} className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 space-y-3">
          <h3 className="text-sm font-semibold text-emerald-800">{editing ? 'Edit Muzakki' : 'Tambah Muzakki'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-stone-500 mb-1">Nama Kepala Keluarga *</label>
              <input value={form.nama_kk} onChange={e => setForm(f => ({ ...f, nama_kk: e.target.value }))}
                className={cls} placeholder="Nama lengkap KK" required />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1">No. HP</label>
              <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                className={cls} placeholder="08xxxxxxxxxx" />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1">Alamat</label>
              <input value={form.alamat} onChange={e => setForm(f => ({ ...f, alamat: e.target.value }))}
                className={cls} placeholder="Alamat tinggal" />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1">Jumlah Jiwa</label>
              <input type="number" min="1" max="50" value={form.jumlah_jiwa}
                onChange={e => setForm(f => ({ ...f, jumlah_jiwa: parseInt(e.target.value) || 1 }))}
                className={cls} />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setShowForm(false)}
              className="px-4 py-2 rounded-xl border border-stone-200 text-sm text-stone-600 hover:bg-stone-50">Batal</button>
            <button type="submit" disabled={saving}
              className="px-5 py-2 bg-emerald-700 text-white rounded-xl text-sm font-medium disabled:opacity-60">
              {saving ? 'Menyimpan...' : 'Simpan'}
            </button>
          </div>
        </form>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-stone-100 flex items-center gap-2">
          <Users className="w-4 h-4 text-emerald-600" />
          <span className="text-sm font-semibold text-stone-700">{total} Muzakki</span>
        </div>
        {data.length === 0 ? (
          <div className="p-10 text-center text-stone-400 text-sm">Belum ada data muzakki</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-xs text-stone-500">
                <tr>
                  <th className="px-4 py-3 text-left">Nama KK</th>
                  <th className="px-4 py-3 text-left">No. HP</th>
                  <th className="px-4 py-3 text-left">Alamat</th>
                  <th className="px-4 py-3 text-center">Jiwa</th>
                  <th className="px-4 py-3 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {data.map(row => (
                  <tr key={row.id} className="hover:bg-stone-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-stone-800">{row.nama_kk}</td>
                    <td className="px-4 py-3 text-stone-600">{row.phone || '-'}</td>
                    <td className="px-4 py-3 text-stone-500 max-w-[150px] truncate">{row.alamat || '-'}</td>
                    <td className="px-4 py-3 text-center font-semibold text-emerald-700">{row.jumlah_jiwa}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => openEdit(row)} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-700 transition-colors" title="Edit">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleDelete(row.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors" title="Hapus">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {total > LIMIT && (
          <div className="px-5 py-3 border-t border-stone-100 flex items-center justify-between text-xs text-stone-500">
            <span>Hal. {page} dari {Math.ceil(total / LIMIT)}</span>
            <div className="flex gap-2">
              {page > 1 && <button onClick={() => load(page - 1)} className="px-3 py-1.5 rounded-lg border border-stone-200 hover:bg-stone-50">← Prev</button>}
              {page < Math.ceil(total / LIMIT) && <button onClick={() => load(page + 1)} className="px-3 py-1.5 rounded-lg border border-stone-200 hover:bg-stone-50">Next →</button>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
